import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import AgentLayout from './AgentLayout';
import ActionMenu from './ActionMenu';

const fmtINR = (n) => (n ? '₹' + Number(n).toLocaleString('en-IN') : '—');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');

const waLink = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? '91' + digits : digits}`;
};


const PILL = 'inline-flex items-center justify-center gap-1 whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold transition disabled:opacity-50 disabled:cursor-not-allowed';
const PILL_GREEN = `${PILL} bg-[#ECFDF5] text-[#10B981] hover:bg-emerald-100`;
const PILL_TEAL = `${PILL} bg-teal-50 text-teal-600 hover:bg-teal-100`;
const PILL_PURPLE = `${PILL} bg-purple-50 text-[#7C3AED] hover:bg-purple-100`;
const PILL_BLUE = `${PILL} bg-blue-50 text-blue-600 hover:bg-blue-100`;
const PILL_RED = `${PILL} bg-[#FEF2F2] text-[#EF4444] hover:bg-red-100`;
const PILL_DISABLED = `${PILL} bg-gray-50 text-gray-300 cursor-default`;

const STATUS_STYLES = {
  active: 'bg-green-50 text-green-600 border border-green-200',
  closed: 'bg-gray-100 text-gray-500 border border-gray-200',
  paused: 'bg-amber-50 text-amber-600 border border-amber-200',
  draft: 'bg-purple-50 text-purple-600 border border-purple-200',
};

const JOB_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'closed', label: 'Closed' },
  { value: 'draft', label: 'Draft' },
];

const JOB_METRIC_COLUMNS = [
  { key: 'openings', label: 'Openings' },
  { key: 'impressions', label: 'Impressions' },
  { key: 'clicks', label: 'Clicks' },
  { key: 'applicants', label: 'Applicants' },
  { key: 'screening', label: 'Screening' },
  { key: 'shortlisted', label: 'Shortlisted' },
  { key: 'interview_round_1', label: 'Interview R1' },
  { key: 'interview_round_2', label: 'Interview R2' },
  { key: 'hr_round', label: 'HR Round' },
  { key: 'offer', label: 'Job Offered' },
  { key: 'joined', label: 'Joined' },
  { key: 'rejected', label: 'Rejected' },
];

const jobMetricValue = (j, key) => {
  if (key === 'applicants') return j.applications_count ?? j.job_applications_count ?? 0;
  if (key === 'openings' || key === 'impressions' || key === 'clicks') return j[key] ?? 0;
  return j.funnel?.[key] ?? 0;
};

const inDateRange = (value, from, to) => {
  if (!from && !to) return true;
  if (!value) return false;
  const d = new Date(value).setHours(0, 0, 0, 0);
  if (from && d < new Date(from).setHours(0, 0, 0, 0)) return false;
  if (to && d > new Date(to).setHours(23, 59, 59, 999)) return false;
  return true;
};

function formatAggSalary(min, max, currency) {
  if (!min && !max) return '—';
  const symbol = currency === 'INR' ? '₹' : (currency ? `${currency} ` : '');
  if (min && max) return `${symbol}${Number(min).toLocaleString('en-IN')} – ${symbol}${Number(max).toLocaleString('en-IN')}`;
  return `${symbol}${Number(min || max).toLocaleString('en-IN')}`;
}

const FILTER_INPUT_CLS =
  'w-full border border-purple-200 rounded-full px-3 py-1.5 text-xs text-gray-700 placeholder:text-gray-400 bg-white ' +
  'hover:border-purple-400 transition focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';
const FILTER_LABEL_CLS = 'block text-[10px] font-semibold uppercase tracking-wide text-purple-600 mb-1 ml-1';
const FILTER_SELECT_CLS =
  'w-full appearance-none cursor-pointer border border-purple-200 rounded-full pl-3 pr-8 py-1.5 text-xs font-medium text-purple-700 ' +
  'bg-gradient-to-r from-purple-50 to-pink-50 hover:border-purple-400 transition ' +
  'focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';

function SearchableSelect({ value, onChange, options, allLabel }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => o.toLowerCase().includes(s)) : options;
  }, [options, q]);

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setQ('');
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={FILTER_SELECT_CLS + ' text-left truncate'}
      >
        {value || allLabel}
      </button>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[200px] bg-white border border-purple-100 rounded-xl shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search..."
              className="w-full border border-purple-200 rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            <button
              type="button"
              onClick={() => pick('')}
              className="block w-full text-left px-3 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-50"
            >
              {allLabel}
            </button>
            {filtered.map((o) => (
              <button
                type="button"
                key={o}
                onClick={() => pick(o)}
                className={`block w-full text-left px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-600 ${
                  o === value ? 'bg-purple-50 text-purple-600 font-semibold' : 'text-gray-700'
                }`}
              >
                {o}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-400">No matches</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MultiSelect({ value, onChange, options, allLabel }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => o.toLowerCase().includes(s)) : options;
  }, [options, q]);

  const toggle = (o) =>
    onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o]);

  const label =
    value.length === 0 ? allLabel : value.length === 1 ? value[0] : `${value.length} selected`;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={FILTER_SELECT_CLS + ' text-left truncate'}
      >
        {label}
      </button>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[220px] bg-white border border-purple-100 rounded-xl shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search..."
              className="w-full border border-purple-200 rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {value.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="block w-full text-left px-3 py-2 text-sm font-semibold text-pink-600 hover:bg-purple-50"
              >
                Clear selection
              </button>
            )}
            {filtered.map((o) => (
              <label
                key={o}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-purple-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={value.includes(o)}
                  onChange={() => toggle(o)}
                  className="h-4 w-4 rounded border-gray-300 accent-purple-600"
                />
                <span className="truncate">{o}</span>
              </label>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-400">No matches</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
const JOB_SORT_GETTERS = {
  title: (j) => (j.title || '').toLowerCase(),
  employer: (j) => (j.employer_name || '').toLowerCase(),
  location: (j) => (j.location || '').toLowerCase(),
  type: (j) => (j.job_type || '').toLowerCase(),
  salary: (j) => Number(j.salary_max || j.salary_min || 0),
  status: (j) => (j.status || '').toLowerCase(),
  posted: (j) => (j.created_at ? new Date(j.created_at).getTime() : 0),
  postedBy: (j) => (j.posted_by_agent_name || '').toLowerCase(),
};
const TEXT_SORT_KEYS = ['title', 'employer', 'location', 'type', 'status', 'postedBy'];
const jobSortValue = (j, key) =>
  JOB_SORT_GETTERS[key] ? JOB_SORT_GETTERS[key](j) : Number(jobMetricValue(j, key)) || 0;

function useDebouncedValue(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// Generic searchable dropdown — used for the Employer filter.
// options: [{ value, label }]
function SearchableCombobox({ value, onChange, options, placeholder, allLabel }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  const selectedLabel = options.find((o) => String(o.value) === String(value))?.label || '';

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    if (!open || !query) return options;
    return options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));
  }, [options, query, open]);

  return (
    <div className="relative" ref={wrapRef}>
      <input
        type="text"
        placeholder={placeholder}
        value={open ? query : selectedLabel}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={(e) => setQuery(e.target.value)}
        className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white w-full focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
      />
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-lg">
          <button
            type="button"
            onClick={() => { onChange(''); setOpen(false); }}
            className="block w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-purple-50 hover:text-purple-600"
          >
            {allLabel}
          </button>
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-400">No matches</div>
          )}
          {filtered.map((o) => (
            <button
              type="button"
              key={o.value}
              onClick={() => { onChange(o.value); setOpen(false); }}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-600"
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Free-text input with suggestion dropdown — used for the City filter.
function CitySearchInput({ value, onChange, suggestions, placeholder }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const base = value
      ? suggestions.filter((c) => c.toLowerCase().includes(value.toLowerCase()))
      : suggestions;
    return base.slice(0, 8);
  }, [suggestions, value]);

  return (
    <div className="relative" ref={wrapRef}>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onFocus={() => setOpen(true)}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white w-full focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-lg">
          {filtered.map((c) => (
            <button
              type="button"
              key={c}
              onClick={() => { onChange(c); setOpen(false); }}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-600"
            >
              {c}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Location input with master-city suggestions ("City, State")
function LocationSuggestInput({ value, onChange }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const q = (value || '').trim();
    if (q.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.post('/ai/suggest-cities', { query: q });
        setSuggestions(res.data?.suggestions || []);
      } catch (err) {
        setSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [value]);

  return (
    <div className="relative">
      <input
        type="text"
        value={value || ''}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        autoComplete="off"
        placeholder="e.g. Bengaluru, Karnataka"
        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-lg">
          {suggestions.map((s) => (
            <button
              type="button"
              key={s}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(s); setOpen(false); }}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-600"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AgentJobsList() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('internal'); // 'internal' | 'aggregated'
  const [viewJob, setViewJob] = useState(null);
  const [editJob, setEditJob] = useState(null);
    const [reasonJob, setReasonJob] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [jobs, setJobs] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [aggJobs, setAggJobs] = useState([]);
  const [aggLoading, setAggLoading] = useState(true);
  const [aggLoadError, setAggLoadError] = useState(false);
  const [aggDeletingId, setAggDeletingId] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    city: '',
    employer_id: '',
    job_type: '',
    status: '',
  });

  const [cityFilter, setCityFilter] = useState([]);
  const [employerFilter, setEmployerFilter] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  
  const debouncedSearch = useDebouncedValue(searchInput, 350);

  // Bulk status update (internal jobs only)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkApplying, setBulkApplying] = useState(false);


    const [funnelFilter, setFunnelFilter] = useState('');
  const [postedByFilter, setPostedByFilter] = useState('');
    const [agentFilter, setAgentFilter] = useState('');
  const [stateFilter, setStateFilter] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [jobSort, setJobSort] = useState({ key: '', dir: 'desc' });
  const [jobPage, setJobPage] = useState(1);
  const [jobPageSize, setJobPageSize] = useState(25);
  const [metricModal, setMetricModal] = useState(null);
  const [metricUsers, setMetricUsers] = useState([]);
  const [metricLoading, setMetricLoading] = useState(false);
  const [metricError, setMetricError] = useState('');
    const [profileUser, setProfileUser] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
   const [employerProfile, setEmployerProfile] = useState(null);




  useEffect(() => {
    setFilters((f) => ({ ...f, search: debouncedSearch }));
  }, [debouncedSearch]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [filters, activeTab, cityFilter, stateFilter, employerFilter]);

  const knownCities = useMemo(() => {
    const set = new Set();
    jobs.forEach((j) => j.location && set.add(j.location));
    aggJobs.forEach((j) => j.location && set.add(j.location));
    return Array.from(set).sort();
  }, [jobs, aggJobs]);

    const knownAgents = useMemo(
    () => Array.from(new Set(jobs.map((j) => j.posted_by_agent_name).filter(Boolean))).sort(),
    [jobs]
  );

  const jobState = (j) => ((j.location || '').split(',')[1] || '').trim();

  const knownStates = useMemo(() => {
    const set = new Set();
    jobs.forEach((j) => { const s = jobState(j); if (s) set.add(s); });
    aggJobs.forEach((j) => { const s = jobState(j); if (s) set.add(s); });
    return Array.from(set).sort();
  }, [jobs, aggJobs]);

  const loadEmployers = useCallback(async () => {
    try {
      const res = await api.get('/agent/employers');
      setEmployers(res.data);
    } catch (err) {
      console.error('Failed to load employers for filter', err);
    }
  }, []);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== '')
      );
      const res = await api.get('/agent/jobs', { params });
      setJobs(res.data);
    } catch (err) {
      console.error('Failed to load jobs', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadAggJobs = useCallback(async () => {
    setAggLoading(true);
    setAggLoadError(false);
    try {
      const params = {};
      if (filters.city) params.location = filters.city;
      if (filters.search) params.keyword = filters.search;
      const res = await api.get('/aggregated-jobs', { params });
      setAggJobs(Array.isArray(res.data) ? res.data : (res.data?.data ?? []));
    } catch (err) {
      console.error('Failed to load aggregated jobs', err);
      setAggLoadError(true);
    } finally {
      setAggLoading(false);
    }
  }, [filters.city, filters.search]);

  const handleStatusChange = async (jobId, newStatus) => {
    setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, status: newStatus } : j)));
    try {
      await api.put(`/agent/jobs/${jobId}`, { status: newStatus });
    } catch (err) {
      console.error('Failed to update job status', err);
      loadJobs();
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      await api.put(`/agent/jobs/${editJob.id}`, {
        title: editJob.title,
        location: editJob.location,
        job_type: editJob.job_type,
        salary_min: editJob.salary_min,
        salary_max: editJob.salary_max,
        openings: editJob.openings,
        status: editJob.status,
      });
      setJobs((prev) => prev.map((j) => (j.id === editJob.id ? { ...j, ...editJob } : j)));
      setEditJob(null);
    } catch (err) {
      console.error('Failed to save job edits', err);
      alert('Could not save changes. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    loadEmployers();
  }, [loadEmployers]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    loadAggJobs();
  }, [loadAggJobs]);

    useEffect(() => {
    setJobPage(1);
  }, [filters, funnelFilter, postedByFilter, agentFilter, stateFilter, cityFilter, employerFilter, dateFrom, dateTo, jobSort, jobPageSize]);

  const toggleJobSort = (key) => {
    setJobSort((prev) =>
      prev.key === key
  ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
  : { key, dir: TEXT_SORT_KEYS.includes(key) ? 'asc' : 'desc' }
    );
  };

  const openMetricModal = async (job, col) => {
    setMetricModal({ job, key: col.key, label: col.label });
    setMetricUsers([]);
    setMetricError('');
    setMetricLoading(true);
    try {
      const res = await api.get(`/agent/jobs/${job.id}/metric-users`, { params: { metric: col.key } });
      setMetricUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setMetricError(err.response?.data?.message || 'Failed to load users.');
    } finally {
      setMetricLoading(false);
    }
  };

    const viewCandidateProfile = async (id) => {
    setProfileLoading(true);
    try {
      const res = await api.get(`/agent/candidates/${id}/profile`);
      setProfileUser(res.data);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load profile.');
    } finally {
      setProfileLoading(false);
    }
  };

    const openEmployerProfile = async (id) => {
    if (!id) return;
    setProfileLoading(true);
    try {
      const res = await api.get(`/agent/employers/${id}/profile`);
      setEmployerProfile(res.data);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load employer details.');
    } finally {
      setProfileLoading(false);
    }
  };

  const updateFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const clearFilters = () => {
    setFilters({ search: '', city: '', employer_id: '', job_type: '', status: '' });
    setCityFilter([]);
    setEmployerFilter([]);
    setSearchInput('');
    setStateFilter([]);
    setFunnelFilter('');
    setPostedByFilter('');
        setAgentFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters =
    Object.values(filters).some((v) => v !== '') || funnelFilter || postedByFilter || agentFilter || dateFrom || dateTo ||
    stateFilter.length > 0 || cityFilter.length > 0 || employerFilter.length > 0;

  const visibleJobs = useMemo(() => {
    let list = jobs;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      list = list.filter((j) =>
        (j.title || '').toLowerCase().includes(q) ||
        (j.employer_name || '').toLowerCase().includes(q)
      );
    }
    if (stateFilter.length > 0) {
      list = list.filter((j) => stateFilter.includes(jobState(j)));
    }
    if (cityFilter.length > 0) {
      list = list.filter((j) => cityFilter.includes(j.location));
    }
    if (employerFilter.length > 0) {
      list = list.filter((j) => employerFilter.includes(j.employer_name));
    }
    if (funnelFilter) {
      list = list.filter((j) => Number(jobMetricValue(j, funnelFilter)) > 0);
    }

        if (postedByFilter) {
      list = list.filter((j) =>
        postedByFilter === 'agent' ? j.posted_by_agent_id != null : j.posted_by_agent_id == null
      );
    }
        if (agentFilter) {
      list = list.filter((j) => j.posted_by_agent_name === agentFilter);
    }

    if (dateFrom || dateTo) {
      list = list.filter((j) => inDateRange(j.created_at, dateFrom, dateTo));
    }
    if (jobSort.key) {
     list = list.slice().sort((a, b) => {
  const x = jobSortValue(a, jobSort.key);
  const y = jobSortValue(b, jobSort.key);
  const r = typeof x === 'number' ? x - y : String(x).localeCompare(String(y));
  return jobSort.dir === 'asc' ? r : -r;
});
    }
    return list;
  }, [jobs, filters.search, funnelFilter, postedByFilter, agentFilter, stateFilter, cityFilter, employerFilter, dateFrom, dateTo, jobSort]);

  const jobPages = Math.max(1, Math.ceil(visibleJobs.length / jobPageSize));
  const curJobPage = Math.min(jobPage, jobPages);
  const pagedJobs = visibleJobs.slice((curJobPage - 1) * jobPageSize, curJobPage * jobPageSize);

  const visibleAggJobs = useMemo(() => {
    let list = aggJobs;
    if (filters.job_type) list = list.filter((j) => j.job_type === filters.job_type);
    if (stateFilter.length > 0) list = list.filter((j) => stateFilter.includes(jobState(j)));
    if (cityFilter.length > 0) list = list.filter((j) => cityFilter.includes(j.location));
    if (filters.search) {
      const q = filters.search.toLowerCase();
      list = list.filter((j) =>
        (j.title || '').toLowerCase().includes(q) ||
        (j.company_name || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [aggJobs, filters.job_type, filters.search, stateFilter, cityFilter]);

  const allVisibleSelected = pagedJobs.length > 0 && pagedJobs.every((j) => selectedIds.has(j.id));

  const toggleSelectAll = () => {
    setSelectedIds(allVisibleSelected ? new Set() : new Set(pagedJobs.map((j) => j.id)));
  };

  const handleBulkStatusApply = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    setBulkApplying(true);
    const ids = Array.from(selectedIds);
    try {
      await Promise.all(ids.map((id) => api.put(`/agent/jobs/${id}`, { status: bulkStatus })));
      setJobs((prev) => prev.map((j) => (ids.includes(j.id) ? { ...j, status: bulkStatus } : j)));
      setSelectedIds(new Set());
      setBulkStatus('');
    } catch (err) {
      console.error('Bulk status update failed', err);
      alert('Some jobs failed to update. Please refresh and try again.');
      loadJobs();
    } finally {
      setBulkApplying(false);
    }
  };

  const sortableTh = (label, key) => (
    <th
      onClick={() => toggleJobSort(key)}
      className="px-5 py-3 font-medium cursor-pointer select-none hover:text-purple-600 whitespace-nowrap"
    >
      {label}
      {jobSort.key === key ? (jobSort.dir === 'asc' ? ' ▲' : ' ▼') : ' ↕'}
    </th>
  );

  return (
  <AgentLayout>
    <div className="-m-4 sm:-m-6 p-4 sm:p-6 min-h-[calc(100vh-57px)] bg-gradient-to-br from-[#FAF5FC] to-[#FDF1F9]">
      <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Jobs</h2>
      <p className="text-gray-500 text-sm mb-6">All job postings in the system.</p>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-purple-100">
        <button
          onClick={() => setActiveTab('internal')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
            activeTab === 'internal'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
        >
          Employer Posted {!loading && !loadError && `(${visibleJobs.length})`}
        </button>
        <button
          onClick={() => setActiveTab('aggregated')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
            activeTab === 'aggregated'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
        >
          Aggregated {!aggLoading && !aggLoadError && `(${visibleAggJobs.length})`}
        </button>
      </div>

        <div className="bg-white rounded-2xl shadow-sm border border-purple-100 p-3 mb-4">
  <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2">
            <label className={FILTER_LABEL_CLS}>Search</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm pointer-events-none">🔍</span>
              <input
                type="text"
                placeholder="Search title, company…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={FILTER_INPUT_CLS + ' pl-10'}
              />
            </div>
          </div>

          <div>
            <label className={FILTER_LABEL_CLS}>{activeTab === 'aggregated' ? 'Location' : 'City'}</label>
            <MultiSelect
              value={cityFilter}
              onChange={setCityFilter}
              options={knownCities}
              allLabel={activeTab === 'aggregated' ? 'All Locations' : 'All Cities'}
            />
          </div>

          <div>
            <label className={FILTER_LABEL_CLS}>State</label>
            <MultiSelect
              value={stateFilter}
              onChange={setStateFilter}
              options={knownStates}
              allLabel="All States"
            />
          </div>

          {activeTab === 'internal' && (
            <div>
              <label className={FILTER_LABEL_CLS}>Employer</label>
              <MultiSelect
                value={employerFilter}
                onChange={setEmployerFilter}
                options={Array.from(new Set(employers.map((e) => e.name).filter(Boolean))).sort()}
                allLabel="All Employers"
              />
            </div>
          )}

          <div>
            <label className={FILTER_LABEL_CLS}>Job Type</label>
            <div className="relative">
              <select
                value={filters.job_type}
                onChange={(e) => updateFilter('job_type', e.target.value)}
                className={FILTER_SELECT_CLS}
              >
                {JOB_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
            </div>
          </div>

          {activeTab === 'internal' && (
            <>
              <div>
                <label className={FILTER_LABEL_CLS}>Status</label>
                <div className="relative">
                  <select
                    value={filters.status}
                    onChange={(e) => updateFilter('status', e.target.value)}
                    className={FILTER_SELECT_CLS}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
                </div>
              </div>

              <div>
                <label className={FILTER_LABEL_CLS}>Funnel Stage</label>
                <div className="relative">
                  <select
                    title="Show only jobs that have at least one candidate at this stage"
                    value={funnelFilter}
                    onChange={(e) => setFunnelFilter(e.target.value)}
                    className={FILTER_SELECT_CLS}
                  >
                    <option value="">All</option>
                    {JOB_METRIC_COLUMNS.filter((c) => c.key !== 'openings').map((c) => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
                </div>
              </div>

              <div>
                <label className={FILTER_LABEL_CLS}>Posted By</label>
                <div className="relative">
                  <select
                    value={postedByFilter}
                    onChange={(e) => setPostedByFilter(e.target.value)}
                    className={FILTER_SELECT_CLS}
                  >
                    <option value="">All</option>
                    <option value="agent">Posted by agent</option>
                    <option value="employer">Posted by employer</option>
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
                </div>
              </div>
              <div>
                <label className={FILTER_LABEL_CLS}>Agent</label>
                <div className="relative">
                  <select
                    value={agentFilter}
                    onChange={(e) => setAgentFilter(e.target.value)}
                    className={FILTER_SELECT_CLS}
                  >
                    <option value="">All Agents</option>
                    {knownAgents.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-purple-500 text-xs">▼</span>
                </div>
              </div>
              <div>
                <label className={FILTER_LABEL_CLS}>Posted From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className={FILTER_INPUT_CLS}
                />
              </div>

              <div>
                <label className={FILTER_LABEL_CLS}>Posted To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className={FILTER_INPUT_CLS}
                />
              </div>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-purple-50">
          <div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-xs font-semibold px-3 py-1.5 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-sm hover:brightness-110 active:scale-95 transition"
              >
                ✕ Clear filters
              </button>
            )}
          </div>
          <p className="text-xs text-gray-400">
            Showing{' '}
            <span className="font-semibold text-purple-600">
              {activeTab === 'internal' ? visibleJobs.length : visibleAggJobs.length}
            </span>{' '}
            of {activeTab === 'internal' ? jobs.length : aggJobs.length} jobs
          </p>
        </div>
      </div>


            {activeTab === 'internal' && !loading && !loadError && (
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2 text-sm text-gray-600">
          <span className="font-semibold">
            Showing {visibleJobs.length === 0 ? 0 : (curJobPage - 1) * jobPageSize + 1}–{Math.min(curJobPage * jobPageSize, visibleJobs.length)} of {visibleJobs.length}
          </span>
          <select
            value={jobPageSize}
            onChange={(e) => setJobPageSize(Number(e.target.value))}
            className="border border-purple-200 rounded-full px-2 py-1 text-xs bg-white"
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>{n} / page</option>
            ))}
          </select>
        </div>
      )}

      {activeTab === 'internal' && selectedIds.size > 0 && (
        <div className="flex items-center gap-3 bg-purple-50 border border-purple-100 rounded-xl px-4 py-2.5 mb-4">
          <span className="text-xs font-semibold text-gray-600">{selectedIds.size} selected</span>
          <select
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
          >
            <option value="">Set status…</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="closed">Closed</option>
            <option value="draft">Draft</option>
          </select>
          <button
            onClick={handleBulkStatusApply}
            disabled={!bulkStatus || bulkApplying}
            className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-5 py-2 rounded-full text-sm font-semibold shadow-sm hover:opacity-90 disabled:opacity-50 transition"
          >
            {bulkApplying ? 'Applying…' : 'Apply'}
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
        </div>
      )}

      {activeTab === 'internal' ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm min-w-[2250px]">
            <thead>
              <tr className="text-left text-gray-400 text-[10px] uppercase border-b border-gray-100 bg-gray-50/60">
                <th className="px-5 py-3 font-medium w-8">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    className="accent-purple-600"
                  />
                </th>
               {sortableTh('Title', 'title')}
{sortableTh('Employer', 'employer')}
{sortableTh('Location', 'location')}
{sortableTh('Type', 'type')}
{sortableTh('Salary', 'salary')}
                {JOB_METRIC_COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => toggleJobSort(col.key)}
                    className="px-5 py-3 font-medium cursor-pointer select-none hover:text-purple-600 whitespace-nowrap"
                  >
                    {col.label}
                    {jobSort.key === col.key ? (jobSort.dir === 'asc' ? ' ▲' : ' ▼') : ' ↕'}
                  </th>
                ))}
               {sortableTh('Status', 'status')}
{sortableTh('Posted', 'posted')}
{sortableTh('Posted By', 'postedBy')}
                <th className="px-5 py-3 font-medium sticky right-0 z-20 bg-gray-50 border-l border-gray-100 shadow-[-6px_0_8px_-4px_rgba(0,0,0,0.08)]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="py-6 text-center text-gray-400">Loading…</td></tr>
              )}
              {!loading && loadError && (
                <tr><td colSpan={10} className="py-6 text-center text-red-500">
                  Could not load jobs. <button onClick={() => loadJobs()} className="underline">Retry</button>
                </td></tr>
              )}
              {!loading && !loadError && visibleJobs.length === 0 && (
                <tr><td colSpan={10} className="py-6 text-center text-gray-400">No job postings match these filters.</td></tr>
              )}
              {!loading && !loadError && pagedJobs.map((j) => (
                <tr key={j.id} className="border-t border-gray-50 hover:bg-purple-50/30 transition-colors">
                  <td className="px-5 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(j.id)}
                      onChange={() => toggleSelect(j.id)}
                      className="accent-purple-600"
                    />
                  </td>
                  <td className="px-5 py-3 font-medium text-gray-800">{j.title}</td>
                  <td className="px-5 py-3">
                    {j.employer_id ? (
                      <button
                        type="button"
                        onClick={() => openEmployerProfile(j.employer_id)}
                        className="text-purple-600 hover:underline text-left"
                      >
                        {j.employer_name}
                      </button>
                    ) : j.employer_name}
                  </td>
                  <td className="px-5 py-3">{j.location || '—'}</td>
                  <td className="px-5 py-3 capitalize">{(j.job_type || '').replace('_', ' ')}</td>
                  <td className="px-5 py-3">
                    {fmtINR(j.salary_min)} – {fmtINR(j.salary_max)}
                  </td>
                  {JOB_METRIC_COLUMNS.map((col) => {
                    const val = jobMetricValue(j, col.key);
                    if (col.key === 'openings') {
                      return (
                        <td key={col.key} className="px-5 py-3 text-center text-xs font-semibold text-gray-700">
                          {j.openings ?? '—'}
                        </td>
                      );
                    }
                    return (
                      <td key={col.key} className="px-5 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => openMetricModal(j, col)}
                          disabled={!Number(val)}
                          className="min-w-[2rem] text-xs font-semibold px-2 py-1 rounded-full bg-purple-50 text-purple-600 hover:bg-purple-100 disabled:bg-transparent disabled:text-gray-400 disabled:cursor-default"
                        >
                          {val}
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-5 py-3">
                    <select
                      value={j.status || ''}
                      onChange={(e) => handleStatusChange(j.id, e.target.value)}
                      className={`text-[10px] font-semibold rounded-full px-2 py-1 border-0 capitalize cursor-pointer whitespace-nowrap ${STATUS_STYLES[j.status] || 'bg-gray-50 text-gray-500'}`}
                    >
                      <option value="active">Active</option>
                      <option value="paused">Paused</option>
                      <option value="closed">Closed</option>
                      <option value="draft">Draft</option>
                    </select>
                    {j.status === 'closed' && j.close_reason && (
                      <button
                        type="button"
                        onClick={() => setReasonJob(j)}
                        className="mt-1 block text-[10px] font-semibold text-purple-600 hover:underline"
                      >
                        View reason
                      </button>
                    )}
                  </td>
                  <td className="px-5 py-3">{fmtDate(j.created_at)}</td>
                                    <td className="px-5 py-3">
                    {j.posted_by_agent_name ? (
                      <span className="text-xs font-semibold px-2 py-1 rounded-full bg-pink-50 text-pink-600">
                        {j.posted_by_agent_name}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Employer</span>
                    )}
                  </td>
                 <td className="px-5 py-3 sticky right-0 z-10 bg-white border-l border-gray-100 shadow-[-6px_0_8px_-4px_rgba(0,0,0,0.08)]">
  <ActionMenu
    phone={j.employer_phone}
    items={[
      { label: 'View', icon: '👁', tone: 'purple', onClick: () => setViewJob(j) },
      { label: 'Edit', icon: '✏️', tone: 'blue', onClick: () => setEditJob({ ...j }) },
    ]}
  />
</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 text-[10px] uppercase border-b border-gray-100 bg-gray-50/60">
                <th className="px-5 py-3 font-medium">Title</th>
                <th className="px-5 py-3 font-medium">Company</th>
                <th className="px-5 py-3 font-medium">Location</th>
                <th className="px-5 py-3 font-medium">Type</th>
                <th className="px-5 py-3 font-medium">Salary</th>
                <th className="px-5 py-3 font-medium">Source</th>
                <th className="px-5 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {aggLoading && (
                <tr><td colSpan={7} className="py-6 text-center text-gray-400">Loading…</td></tr>
              )}
              {!aggLoading && aggLoadError && (
                <tr><td colSpan={7} className="py-6 text-center text-red-500">
                  Could not load aggregated jobs. <button onClick={() => loadAggJobs()} className="underline">Retry</button>
                </td></tr>
              )}
              {!aggLoading && !aggLoadError && visibleAggJobs.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-gray-400">No aggregated jobs match these filters.</td></tr>
              )}
              {!aggLoading && !aggLoadError && visibleAggJobs.map((j) => (
                <tr key={j.id} className="border-t border-gray-50 hover:bg-purple-50/30 transition-colors">
                  <td className="px-5 py-3 font-medium text-gray-800">{j.title}</td>
                  <td className="px-5 py-3">{j.company_name || '—'}</td>
                  <td className="px-5 py-3">{j.location || '—'}</td>
                  <td className="px-5 py-3 capitalize">{(j.job_type || '').replace(/_/g, ' ') || '—'}</td>
                  <td className="px-5 py-3">{formatAggSalary(j.salary_min, j.salary_max, j.salary_currency)}</td>
                  <td className="px-5 py-3">
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-purple-50 text-purple-600 border border-purple-100">
                      {j.source_name || 'Partner source'}
                    </span>
                  </td>
                 <td className="px-5 py-3">
  <ActionMenu
    hideContact
    items={[{ label: 'View', icon: '👁', tone: 'purple', onClick: () => setViewJob(j) }]}
  />
</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

            {activeTab === 'internal' && !loading && !loadError && (
        <div className="flex items-center justify-between flex-wrap gap-3 mt-3 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <span>
              Showing {visibleJobs.length === 0 ? 0 : (curJobPage - 1) * jobPageSize + 1}–
              {Math.min(curJobPage * jobPageSize, visibleJobs.length)} of {visibleJobs.length}
            </span>
            <select
              value={jobPageSize}
              onChange={(e) => setJobPageSize(Number(e.target.value))}
              className="border rounded-lg px-2 py-1 text-xs"
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>{n} / page</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1">
            {(() => {
              const btn = 'px-3 py-1.5 rounded-lg text-xs font-semibold border disabled:opacity-40';
              const nums = [];
              for (let p = Math.max(1, curJobPage - 2); p <= Math.min(jobPages, curJobPage + 2); p++) nums.push(p);
              return (
                <>
                  <button className={btn} disabled={curJobPage === 1} onClick={() => setJobPage(1)}>«</button>
                  <button className={btn} disabled={curJobPage === 1} onClick={() => setJobPage(curJobPage - 1)}>Prev</button>
                  {nums.map((p) => (
                    <button
                      key={p}
                      onClick={() => setJobPage(p)}
                      className={`${btn} ${p === curJobPage ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-purple-600' : 'bg-white'}`}
                    >
                      {p}
                    </button>
                  ))}
                  <button className={btn} disabled={curJobPage === jobPages} onClick={() => setJobPage(curJobPage + 1)}>Next</button>
                  <button className={btn} disabled={curJobPage === jobPages} onClick={() => setJobPage(jobPages)}>»</button>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {metricModal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setMetricModal(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-3xl w-full p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">{metricModal.label}</h3>
                <p className="text-xs text-gray-500">{metricModal.job.title} · {metricModal.job.employer_name}</p>
              </div>
              <button onClick={() => setMetricModal(null)} className="text-gray-400 hover:text-purple-600 text-xl leading-none">×</button>
            </div>

            {metricLoading ? (
              <p className="text-center text-gray-400 py-8">Loading...</p>
            ) : metricError ? (
              <p className="text-center text-red-600 py-6 text-sm">{metricError}</p>
            ) : metricUsers.length === 0 ? (
              <p className="text-center text-gray-400 py-8 text-sm">No users found.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50/60 text-gray-400 text-[10px] uppercase border-b border-gray-100">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Name</th>
                    <th className="text-left px-3 py-2 font-semibold">Email</th>
                    <th className="text-left px-3 py-2 font-semibold">Phone</th>
                    <th className="text-left px-3 py-2 font-semibold">Location</th>
                    <th className="text-left px-3 py-2 font-semibold">Date</th>
                    <th className="text-left px-3 py-2 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {metricUsers.map((u, i) => (
                    <tr key={`${u.id}-${i}`} className="border-t border-gray-50 hover:bg-purple-50/30">
                      <td className="px-3 py-2 font-medium">{u.name}</td>
                      <td className="px-3 py-2">{u.email}</td>
                      <td className="px-3 py-2">{u.phone || '—'}</td>
                      <td className="px-3 py-2">{u.location || '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(u.at)}</td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => viewCandidateProfile(u.id)}
                          disabled={profileLoading}
                          className="text-xs font-semibold text-purple-600 hover:underline disabled:opacity-50"
                        >
                          View profile
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {profileUser && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
          onClick={() => setProfileUser(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setProfileUser(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-purple-600 text-xl font-bold"
            >
              ×
            </button>

            <div className="space-y-5">
              <div className="flex items-center gap-4">
                {profileUser.profile_photo_path && (
                  <img
                    src={`${BACKEND_URL}/storage/${profileUser.profile_photo_path}`}
                    alt={profileUser.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                )}
                <div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">{profileUser.name}</h3>
                  <p className="text-gray-500 text-sm">{profileUser.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                <p><span className="font-medium">Phone:</span> {profileUser.phone || '—'}</p>
                <p><span className="font-medium">DOB:</span> {profileUser.date_of_birth || '—'}</p>
                <p><span className="font-medium">Gender:</span> {profileUser.gender || '—'}</p>
                <p><span className="font-medium">City:</span> {profileUser.city || '—'}</p>
                <p><span className="font-medium">State:</span> {profileUser.state || '—'}</p>
                <p><span className="font-medium">Pincode:</span> {profileUser.pincode || '—'}</p>
                <p className="col-span-2"><span className="font-medium">Address:</span> {profileUser.address || '—'}</p>
              </div>

              {profileUser.career_objective && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Career Objective:</span> {profileUser.career_objective}
                </p>
              )}

              {profileUser.resume_path && (
                <a
                  href={`${BACKEND_URL}/storage/${profileUser.resume_path}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block text-purple-600 hover:underline text-sm font-medium"
                >
                  View Resume
                </a>
              )}

              {profileUser.skills?.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {profileUser.skills.map((s) => (
                      <span key={s.id} className="bg-purple-50 text-purple-600 text-xs font-medium px-2 py-1 rounded">
                        {s.skill_name || s.name || s.skill || s.title} {s.proficiency ? `(${s.proficiency})` : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {profileUser.educations?.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">Education</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {profileUser.educations.map((e) => (
                      <li key={e.id}>
                        {e.degree} — {e.institution} {e.year_of_passing ? `(${e.year_of_passing})` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {profileUser.experiences?.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">Experience</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {profileUser.experiences.map((exp) => (
                      <li key={exp.id}>{exp.job_title} — {exp.company_name}</li>
                    ))}
                  </ul>
                </div>
              )}

              {profileUser.job_applications?.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">Job Applications</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {profileUser.job_applications.map((app) => (
                      <li key={app.id} className="border-b pb-1">
                        <span className="font-medium">{app.job_posting?.title}</span> at {app.job_posting?.company_name} — <span className="capitalize">{app.status}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {employerProfile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={() => setEmployerProfile(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setEmployerProfile(null)} className="absolute top-4 right-4 text-gray-400 hover:text-purple-600 text-xl font-bold">×</button>
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-4 pr-8">
                <div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">{employerProfile.profile.name}</h3>
                  <p className="text-gray-500 text-sm">{employerProfile.profile.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  {employerProfile.profile.phone && (
                    <a href={`tel:${employerProfile.profile.phone}`} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100">Call</a>
                  )}
                  {waLink(employerProfile.profile.phone) && (
                    <a href={waLink(employerProfile.profile.phone)} target="_blank" rel="noreferrer" className="text-xs font-semibold px-3 py-1.5 rounded-full bg-green-50 text-green-600 hover:bg-green-100">WhatsApp</a>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                <p><span className="font-medium">Phone:</span> {employerProfile.profile.phone || '—'}</p>
                <p><span className="font-medium">City:</span> {employerProfile.profile.city || '—'}</p>
                <p><span className="font-medium">State:</span> {employerProfile.profile.state || '—'}</p>
                <p><span className="font-medium">Website:</span> {employerProfile.profile.company_website || '—'}</p>
                <p><span className="font-medium">GST:</span> {employerProfile.profile.company_gst || '—'}</p>
                <p><span className="font-medium">Size:</span> {employerProfile.profile.company_size || '—'}</p>
                <p><span className="font-medium">Industry:</span> {employerProfile.profile.company_industry || '—'}</p>
                <p><span className="font-medium">Verified:</span> {employerProfile.profile.company_verified_status || 'unverified'}</p>
              </div>
              {employerProfile.profile.company_about && (
                <p className="text-sm text-gray-700"><span className="font-medium">About:</span> {employerProfile.profile.company_about}</p>
              )}
              <div>
                <h4 className="font-bold text-gray-800 mb-2">Job Postings</h4>
                {employerProfile.jobs.length === 0 ? (
                  <p className="text-sm text-gray-400">No jobs posted.</p>
                ) : (
                  <ul className="text-sm text-gray-700 space-y-1">
                    {employerProfile.jobs.map((jb) => (
                      <li key={jb.id} className="border-b pb-1">
                        <span className="font-medium">{jb.title}</span> · {jb.location || '—'} · <span className="capitalize">{jb.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

            {reasonJob && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setReasonJob(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
                Close Reason
              </h3>
              <button
                onClick={() => setReasonJob(null)}
                className="text-gray-400 hover:text-purple-600 text-xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="space-y-2 text-sm">
              <p><span className="text-gray-400">Job:</span> {reasonJob.title}</p>
              <p><span className="text-gray-400">Employer:</span> {reasonJob.employer_name || '—'}</p>
              <p className="whitespace-pre-wrap break-words">
                <span className="text-gray-400">Reason:</span> {reasonJob.close_reason}
              </p>
              <p>
                <span className="text-gray-400">Closed on:</span>{' '}
                {reasonJob.closed_at
                  ? new Date(reasonJob.closed_at).toLocaleString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '—'}
              </p>
            </div>
          </div>
        </div>
      )}

      {viewJob && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setViewJob(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">{viewJob.title}</h3>
              <button onClick={() => setViewJob(null)} className="text-gray-400 hover:text-purple-600 text-xl leading-none">×</button>
            </div>
            <div className="space-y-2 text-sm">
              {viewJob.employer_name && <p><span className="text-gray-400">Employer:</span> {viewJob.employer_name}</p>}
              {viewJob.company_name && <p><span className="text-gray-400">Company:</span> {viewJob.company_name}</p>}
              <p><span className="text-gray-400">Location:</span> {viewJob.location || '—'}</p>
              <p><span className="text-gray-400">Type:</span> {(viewJob.job_type || '').replace(/_/g, ' ') || '—'}</p>
              <p><span className="text-gray-400">Salary:</span> {fmtINR(viewJob.salary_min)} – {fmtINR(viewJob.salary_max)}</p>
              {viewJob.openings !== undefined && <p><span className="text-gray-400">Openings:</span> {viewJob.openings ?? '—'}</p>}
              {viewJob.status && <p><span className="text-gray-400">Status:</span> <span className="capitalize">{viewJob.status}</span></p>}
                            {viewJob.status === 'closed' && viewJob.close_reason && (
                <p><span className="text-gray-400">Close Reason:</span> {viewJob.close_reason}</p>
              )}
              {viewJob.created_at && <p><span className="text-gray-400">Posted:</span> {fmtDate(viewJob.created_at)}</p>}
              {viewJob.description && (
                <div>
                  <p className="text-gray-400 mb-1">Description:</p>
                  <p className="whitespace-pre-wrap text-gray-700">{viewJob.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {editJob && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setEditJob(null)}
        >
          <form
            onSubmit={handleSaveEdit}
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">Edit Job</h3>
              <button type="button" onClick={() => setEditJob(null)} className="text-gray-400 hover:text-purple-600 text-xl leading-none">×</button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Title</label>
                <input
                  type="text"
                  value={editJob.title || ''}
                  onChange={(e) => setEditJob((j) => ({ ...j, title: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
                />
              </div>
              <div>
  <label className="block text-xs text-gray-400 mb-1">Location</label>
  <LocationSuggestInput
    value={editJob.location}
    onChange={(v) => setEditJob((j) => ({ ...j, location: v }))}
  />
</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Job Type</label>
                  <select
                    value={editJob.job_type || ''}
                    onChange={(e) => setEditJob((j) => ({ ...j, job_type: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
                  >
                    {JOB_TYPE_OPTIONS.filter((o) => o.value).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Status</label>
                  <select
                    value={editJob.status || ''}
                    onChange={(e) => setEditJob((j) => ({ ...j, status: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
                  >
                    {STATUS_OPTIONS.filter((o) => o.value).map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Salary Min</label>
                  <input
                    type="number"
                    value={editJob.salary_min ?? ''}
                    onChange={(e) => setEditJob((j) => ({ ...j, salary_min: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Salary Max</label>
                  <input
                    type="number"
                    value={editJob.salary_max ?? ''}
                    onChange={(e) => setEditJob((j) => ({ ...j, salary_max: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Openings</label>
                  <input
                    type="number"
                    value={editJob.openings ?? ''}
                    onChange={(e) => setEditJob((j) => ({ ...j, openings: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button type="button" onClick={() => setEditJob(null)} className="text-sm text-gray-500 hover:text-gray-700">
                Cancel
              </button>
              <button
                type="submit"
                disabled={savingEdit}
                className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-5 py-2 rounded-full text-sm font-semibold shadow-sm hover:opacity-90 disabled:opacity-50 transition"
              >
                {savingEdit ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      )}
     </div>
    </AgentLayout>
  );
}

export default AgentJobsList;